<template>
  <div class="page-details">

    <layout-teleport-to-breadcrumb>
      <layout-breadcrumb-loader
        :predicate="
          fetchState.status.value === 'success' &&
          companyListFix.isPending.value === false
        "
      />
    </layout-teleport-to-breadcrumb>


    <ui-cloak
      :is-visible="
        fetchState.status.value === 'success' &&
        companyListFix.isPending.value === false
      "
      :sustain-phase-duration="300"
    >
      <template #default>

        <div class="page-invitation__subheader">

          <ui-link-button-meek
            :size="UI.SIZE.SMALL"
            :route="parentRoute"
            bulky
          >
            <ui-icon
              name="navigate_before"
            />
            Utilisateurs
          </ui-link-button-meek>

        </div>

        <div class="page-details__main">
          <details-organism
            v-bind="{
              config,
              companyList: companyListFix.companyList.value,
              defaultData,
              callback: updateCallback,
              mode: 'edit',
              clientAccountCode: fromStructureLevel().structure.code_client,
            }"
          />
        </div>

      </template>
    </ui-cloak>

  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/variables'

.page-details,
.page-details__main
  height: 100%
  background-color: $background-secondary

.page-invitation

  &__subheader
    padding: $layout-spacer-y $layout-spacer-x 0

</style>

<script lang="ts">
import DetailsOrganism from '~/components/parameters/user/details/Organism.vue'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useLinkHandler } from '~/composables/core/link-handler'
import { useDataOnlyNavigationContext } from '~/composables/layout/navigation'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyListFix } from '~/composables/parameters/company-list-fix'
import { useStore_account_details } from '~/stores/account/details'
import { useStore_client_admin_details, type DetailsConfig, type UserDetails } from '~/stores/client/admin/details'
import { useStore_client_admin_list } from '~/stores/client/admin/list'
import { extractRouteCompanyCode } from '~/stores/layout/navigation'
import Console from '~/utils/lib/core/console'
import { delay, noop, omit, promiseTry, sustainedPromise, tapCatch, toString } from '~/utils/lib/core/prelude'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'

const console = new Console('component:user-invitation')

export default defineNuxtComponent({
  components: { DetailsOrganism },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.DATA_ONLY,
      menuItem: MENU_ITEM.PARAMETERS_USER_MANAGEMENT,
    })
    const navigationContext = useDataOnlyNavigationContext()

    useHead({
      title: `Détails d'un utilisateur — ${navigationContext.fromStructureLevel().structure.raison}`,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const companyListFix = useCompanyListFix()

    const store_account_details = useStore_account_details()
    const store_admin_details = useStore_client_admin_details()
    const store_admin_list = useStore_client_admin_list()

    const {
      start: startLoadingIndicator,
      finish: finishLoadingIndicator,
    } = useLoadingIndicator()
    const company_code = extractRouteCompanyCode(useRoute())

    const parentRoute = store_admin_list.lastNavigatedRoute(
      navigationContext.fromStructureLevel().structure.id,
      company_code
    )

    return {
      ...navigationContext,
      fetchState,
      fetchWrapper,
      parentRoute,
      ToastBuilder,
      store_account_details,
      store_admin_details,
      startLoadingIndicator,
      finishLoadingIndicator,
      ...useLinkHandler(),
      companyListFix,
    }
  },
  data() {
    return {
      config: null as unknown as DetailsConfig,
      defaultData: null as unknown as UserDetails,
    }
  },
  computed: {
    isCurrentAccount() {
      return this.defaultData.compte_id === this.store_account_details.id
    },
  },
  created() {
    this.companyListFix.hydrate()

    this.fetchWrapper(
      () => this.fetch()
    )
  },
  methods: {
    fetch() {
      const {
        uuidData,
      } = this.fromStructureLevel().structure

      const compte_id = this.$route.params.compteId as string

      return Promise.all(
        [
          this.store_admin_details.config({ uuidData }),
          this.store_admin_details.read({ uuidData, compte_id }),
        ]
      )
        .then(([ config, defaultData ]) => {
          this.config = config
          this.defaultData = defaultData
        })
        .catch(tapCatch(err => {
          console.warn('fetch', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Invitation d'un nouvel utilisateur`)
            .show()
        }))
    },
    updateCallback(data :Partial<UserDetails>) {
      const options = {
        data: omit([ 'email', 'compte_id' ], data as UserDetails),
        uuidData: this.fromStructureLevel().structure.uuidData,
        compte_id: this.$route.params.compteId as string,
      }

      return sustainedPromise(
          400,
          this.store_admin_details.update(options)
        )
        .then(() => {
          switch (this.isCurrentAccount) {

            case false:
              /// Detached (knowing some reward UI feedback are made deeply)
              promiseTry(
                  () => this.startLoadingIndicator()
                )
                .then(() => delay(1000))
                .then(() => this.goToRoute( this.parentRoute ))
                .then(() => this.ToastBuilder
                  .success()
                  .title(`Utilisateur modifié !`)
                  .show()
                )
                .catch(noop)
              break

            case true:
              /// Reload whole page while explaning to user that his account
              /// is being updated
              console.info(
                `current account is now being updated,`,
                `automatic page reload will be made`
              )
              promiseTry(
                () => this.startLoadingIndicator()
              )
              .then(() => this.ToastBuilder
                .success(`La page va se recharger automatiquement`)
                .title(`Votre compte à été modifié !`)
                .show()
              )
              .then(() => delay(1700))
              .then(() => this.finishLoadingIndicator())
              .then(() => delay(300))
              .then(() => void(this.goToURL( this.parentRoute )))
              .catch(noop)
          }

        })
    }
  },
})
</script>
